import { formatVisitDateLabel } from "./circumference-utils";
import {
  buildLinearXAxis,
  COMPARISON_SERIES_COLORS,
  createAreaGradient,
  resolveXAxisBounds,
  resolveYAxisBounds,
  timelineToPoints,
} from "./chart-axis-utils";

const buildSeriesDataset = ({ row, points, borderColor }) => ({
  label: row.regionLabel,
  unit: row.unit,
  parsing: false,
  data: points.map((point) => ({ x: point.x, y: point.y })),
  borderColor,
  backgroundColor: (context) => createAreaGradient(context.chart, borderColor),
  fill: false,
  borderWidth: 3,
  borderCapStyle: "round",
  borderJoinStyle: "round",
  cubicInterpolationMode: "monotone",
  tension: 0.35,
  pointRadius: 4,
  pointHoverRadius: 6,
  pointBackgroundColor: "#FFFFFF",
  pointBorderColor: borderColor,
  pointBorderWidth: 2,
});

export const buildMuscleComparisonChartModel = ({ rows = [] } = {}) => {
  const series = rows
    .map((row) => ({
      row,
      points: timelineToPoints(row?.timeline || []),
    }))
    .filter((entry) => entry.points.length > 0);

  if (series.length === 0) {
    return {
      chartData: { datasets: [] },
      chartOptions: {},
      hasValues: false,
    };
  }

  const allPoints = series.flatMap((entry) => entry.points);
  const { xMin, xMax, spanDays, xTicks, monthStarts } = resolveXAxisBounds(allPoints);
  const yValues = allPoints.map((point) => point.y);
  const { suggestedMin, suggestedMax } = resolveYAxisBounds(yValues);
  const unit = series[0].row.unit || "cm";

  const chartData = {
    datasets: series.map((entry, index) =>
      buildSeriesDataset({
        row: entry.row,
        points: entry.points,
        borderColor: COMPARISON_SERIES_COLORS[index % COMPARISON_SERIES_COLORS.length],
      })
    ),
  };

  const visitDateByDatasetIndex = series.map((entry) =>
    Object.fromEntries(entry.points.map((point) => [point.x, point.visitDate]))
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 8,
        top: 8,
        bottom: 0,
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: buildLinearXAxis({ points: allPoints, xMin, xMax, spanDays, xTicks, monthStarts }),
      y: {
        beginAtZero: false,
        suggestedMin,
        suggestedMax,
        border: { display: false },
        grid: {
          color: "#EEF0F3",
          lineWidth: 1,
          drawBorder: false,
        },
        ticks: {
          color: "#9CA3AF",
          font: { size: 12, weight: "500" },
          padding: 10,
          callback: (value) =>
            `${Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${unit}`,
        },
      },
    },
    plugins: {
      legend: {
        display: series.length > 1,
        position: "bottom",
        align: "center",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          padding: 16,
          color: "#6B7280",
          font: {
            size: 13,
            weight: "500",
          },
        },
      },
      tooltip: {
        backgroundColor: "#1F1F23",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        padding: 12,
        cornerRadius: 10,
        displayColors: true,
        callbacks: {
          title: (items) => {
            const timestamp = items?.[0]?.parsed?.x;
            if (timestamp == null) {
              return "";
            }
            const datasetIndex = items[0].datasetIndex;
            const visitDate = visitDateByDatasetIndex[datasetIndex]?.[timestamp];
            return visitDate ? formatVisitDateLabel(visitDate) : "";
          },
          label: (context) => {
            const value = context.parsed.y;
            const seriesUnit = context.dataset.unit || unit;
            if (value == null) {
              return `${context.dataset.label}: —`;
            }
            return `${context.dataset.label}: ${Number(value).toLocaleString("pt-BR", {
              maximumFractionDigits: 1,
            })} ${seriesUnit}`;
          },
        },
      },
    },
  };

  return {
    chartData,
    chartOptions,
    hasValues: true,
  };
};
